/**
 * Currency Helper - Para Birimi Yardımcı Fonksiyonları
 * 
 * Backend'deki standart teknik kodları (örn: USDTRY=X)
 * kullanıcı dostu isimlere (örn: Amerikan Doları) çevirir.
 */
import { getAssetDisplayName } from './assetDisplayNames';

// Kod -> Kısa Kod (Badge için)
export const CURRENCY_SHORT_CODES = {
    'USDTRY=X': 'USD',
    'EURTRY=X': 'EUR',
    'GBPTRY=X': 'GBP',
    'CHFTRY=X': 'CHF',
    'JPYTRY=X': 'JPY',
    'CADTRY=X': 'CAD',
    'AUDTRY=X': 'AUD',
    'SARTRY=X': 'SAR',
    'SEKTRY=X': 'SEK',
    'NOKTRY=X': 'NOK',
    'DKKTRY=X': 'DKK',
    'RUBTRY=X': 'RUB',
    'XAUTRYG': 'GRAM',
    'XAUTRYG_OLD': 'GRAM',
    'XAGTRYG': 'GÜMÜŞ',
    'XAGTRYG_OLD': 'GÜMÜŞ',
    'XAU_QTR': 'ÇEYREK',
    'XAU_QTR_NEW': 'ÇEYREK',
    'XAU_HALF': 'YARIM',
    'XAU_HALF_NEW': 'YARIM',
    'XAU_FULL': 'TAM',
    'XAU_FULL_NEW': 'TAM',
    'XAU_CUM': 'CUM',
    'XAU_CUM_NEW': 'CUM',
    'GRAM': 'GRAM',
    'GRAM_HAS': 'HAS',
    'CEYREK': 'ÇEYREK',
    'YARIM': 'YARIM',
    'TAM': 'TAM',
    'ATA': 'ATA',
    'PLATIN': 'PLATİN',
    'PALADYUM': 'PALADYUM',
    'XAU_GREMSE': 'GREMSE',
    'XAU_GREMSE_NEW': 'GREMSE',
    'XAU=X': 'ONS',
    'XAG=X': 'GÜMÜŞ',
    'XU100.IS': 'BIST',
    'BTC-USD': 'BTC',
    'ETH-USD': 'ETH',
    'BZ=F': 'BRENT'
};

// Kod -> Sembol/Emoji Haritası
export const CURRENCY_ICONS = {
    'USDTRY=X': '$',
    'EURTRY=X': '€',
    'GBPTRY=X': '£',
    'JPYTRY=X': '¥',
    'XU100.IS': '📈',
    'BTC-USD': '₿',
    'ETH-USD': 'Ξ',
    'BZ=F': '🛢️',
    'XAU=X': '🏆',
    'XAUTRYG': '🥇'
};

/**
 * Koddan display name getir
 * @param {string} code - Örn: 'USDTRY=X'
 * @returns {string} - Örn: 'Amerikan Doları'
 */
export const getCurrencyDisplayName = (code) => {
    return getAssetDisplayName(code);
};

/**
 * Koddan piyasa standardı kısa kod getir (Badge için)
 * Örn: USDTRY=X -> USD
 * Örn: EURUSD=X -> EUR/USD
 */
export const getShortLabel = (code) => {
    if (!code) return '';
    const normalizedCode = String(code).toLocaleUpperCase('tr-TR');

    // 1. Emtia ve Kripto Özel Mapping
    const specialMap = {
        'GC=F': 'ONS',
        'XAU=X': 'ONS',
        'SI=F': 'XAG/USD',
        'XAG=X': 'XAG/USD',
        'BZ=F': 'BRENT',
        'BTC-USD': 'BTC',
        'ETH-USD': 'ETH',
        'XU100.IS': 'BIST',
        'XAUTRYG': 'GRAM',
        'XAU_QTR': 'ÇEYREK',
        'XAU_HALF': 'YARIM',
        'XAU_FULL': 'TAM',
        'GRAM': 'GRAM',
        'GRAM_HAS': 'HAS',
        'CEYREK': 'ÇEYREK',
        'YARIM': 'YARIM',
        'TAM': 'TAM',
        'XAU_CUM': 'CUM',
        'ATA': 'ATA',
        'AYAR_14': '14K',
        'AYAR_18': '18K',
        'BILEZIK_22': '22K',
        'GREMSE': 'GREMSE',
        'XAGTRYG': 'GÜMÜŞ',
        'PLATIN': 'PLATİN',
        'PALADYUM': 'PALADYUM'
    };
    if (specialMap[normalizedCode]) return specialMap[normalizedCode];

    // 2. TRY Çiftleri (USDTRY=X -> USD)
    // Eğer kod "TRY=X" ile bitiyorsa, baştaki kısmını al
    if (normalizedCode.endsWith('TRY=X')) {
        return normalizedCode.replace('TRY=X', '');
    }

    // 3. Pariteler (EURUSD=X -> EUR/USD)
    // Eğer kod "=X" ile bitiyorsa ve 8 karaktersen (6 + =X)
    if (normalizedCode.endsWith('=X') && normalizedCode.length === 8) {
        const clean = normalizedCode.replace('=X', '');
        const base = clean.substring(0, 3);
        const quote = clean.substring(3, 6);
        return `${base}/${quote}`;
    }

    // 4. Default
    return normalizedCode;
};

// Legacy support if needed, maps to new function
export const getShortDisplayCode = (code) => getShortLabel(code);

/**
 * Koddan ikon getir
 * @param {string} code 
 * @returns {string}
 */
export const getCurrencyIcon = (code) => {
    return CURRENCY_ICONS[code] || '💰';
};

/**
 * Miktar formatla (Currency + Number)
 */
export const formatCurrency = (amount, code) => {
    if (!amount && amount !== 0) return '-';

    // Eğer kod USD bazlı ise $ işareti, değilse TL
    const isUSD = code && (code.includes('=F') || code.includes('-USD') || code === 'XAU=X' || code === 'XAG=X');

    const formatter = new Intl.NumberFormat('tr-TR', {
        style: 'currency',
        currency: isUSD ? 'USD' : 'TRY',
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
    });

    return formatter.format(amount);
};
/**
 * Akıllı Para Birimi ve Sembol Yönetimi
 * @param {string} code - Para birimi kodu (Örn: 'USDTRY=X', 'XAU=X')
 * @param {number} value - Fiyat değeri (Büyüklük kontrolü için)
 * @returns {object} { symbol, decimals, name }
 */
export const getCurrencySettings = (code, value = 0) => {
    if (!code) return { symbol: '', decimals: 2 };

    // 1. Kripto (Bitcoin, Ethereum) -> Her zaman $
    if (code === 'BTC-USD' || code === 'ETH-USD') {
        return { symbol: '$', decimals: 2 };
    }

    // 2. BIST (Endeks) -> Sembolsüz
    if (code === 'XU100.IS') {
        return { symbol: '', decimals: 0 };
    }

    // 3. Global Emtialar (Değer kontrolü ile)
    const commodities = ['XAU=X', 'XAG=X', 'BZ=F', 'GC=F', 'SI=F', 'CL=F'];
    if (commodities.includes(code)) {
        // Ons Altın (2000-3000 bandı $ dır)
        if (code === 'XAU=X' || code === 'GC=F') {
            return value > 10000 ? { symbol: '₺', decimals: 2 } : { symbol: '$', decimals: 2 };
        }
        // Ons Gümüş (20-50 bandı $ dır)
        if (code === 'XAG=X' || code === 'SI=F') {
            return value > 500 ? { symbol: '₺', decimals: 2 } : { symbol: '$', decimals: 2 };
        }
        // Petrol (Brent ve Ham) (60-100 bandı $ dır)
        if (code === 'BZ=F' || code === 'CL=F') {
            return value > 500 ? { symbol: '₺', decimals: 2 } : { symbol: '$', decimals: 2 };
        }
    }

    // 4. Pariteler (EURUSD=X vb.) -> Sembolsüz veya karşıt dövizi
    // '=X' ile bitip 'TRY' içermeyenler
    if (code.endsWith('=X') && !code.includes('TRY')) {
        return { symbol: '', decimals: 4 };
    }

    // 5. Varsayılan (TRY Çiftleri ve Diğerleri) -> ₺
    return { symbol: '₺', decimals: 4 };
};

/**
 * Legacy wrapper - Sadece sembol döner
 * @deprecated Use getCurrencySettings instead
 */
export const getCurrencySymbol = (code) => {
    return getCurrencySettings(code, 0).symbol;
};
